import { DevTool } from "@hookform/devtools";
import { LoadingButton } from "@mui/lab";
import {
  Typography,
  FormControl,
  TextField,
  Stack,
  Alert,
  Collapse,
  Button,
} from "@mui/material";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CountrySelector from "../../components/country-selector/CountrySelector";
import getCompanyOverview from "../../network/get-company-overview";
import StepTitleView from "../StepTitleView";
import signup from "../../utils/account/signup";
import { ISignUpResult } from "amazon-cognito-identity-js";
import CompanySearchAutocomplete from "./CompanySearchAutocomplete";

interface CompanyInformationProps {
  language: Language;
  country: string;
  onSubmit: (information: CompanyInformation) => void;
  signupInformation: SignupInformation;
  onBack: () => void;
  product?: Product;
}

const CompanyInformation = ({
  language,
  country: initCountry,
  onSubmit,
  signupInformation,
  product,
  onBack,
}: CompanyInformationProps) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<null | string>(null);
  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    trigger,
    setError,
    formState: { isValid, isSubmitting },
  } = useForm<CompanyInformation>({
    mode: "onChange",
    defaultValues: {
      companyId: "",
      companyName: "",
      zip: "",
      streetAddress: "",
      city: "",
      country: initCountry,
      vat: "",
      vatStatus: "EMPTY",
      vatType: "SEVAT",
      status: undefined,
      searchString: {
        id: "",
        label: "",
      },
    },
  });

  const submit: SubmitHandler<CompanyInformation> = async (data) => {
    /**
     * Form has been submitted once already, with an incomplete result.
     * Company information has been manually entered instead of using
     * information from backend. Just pass it on.
     */

    if (data.status === "INCOMPLETE") {
      const companyInfo = {
        ...data,
      };
      signup(
        {
          ...signupInformation,
          ...companyInfo,
        },
        language,
        product?.id || "",
        (error: Error | undefined, result: ISignUpResult | undefined) => {
          if (result) {
            onSubmit(data);
          }
          if (error) {
            setSubmitError(error.message);
          }
        }
      );
      return;
    }
    const regId = data.searchString?.id || data.searchString?.label || "";
    if (!regId) {
      return;
    }
    const information = await getCompanyOverview(data.country, regId);

    const { status, city, companyName, zip, streetAddress, companyId } =
      information.properties;
    setValue("companyName", companyName);
    setValue("streetAddress", streetAddress);
    setValue("zip", zip);
    setValue("city", city);
    setValue("companyId", companyId);
    setValue("status", status);
    if (status === "INCOMPLETE") {
      await trigger();
      return;
    }

    if (status === "OK") {
      signup(
        {
          ...signupInformation,
          ...information.properties,
        },
        language,
        product?.id || "",
        (error: Error | undefined, result: ISignUpResult | undefined) => {
          if (result) {
            onSubmit(information.properties);
          }
          if (error) {
            setSubmitError(error.message);
          }
        }
      );
    }
  };

  const [country, status] = watch(["country", "status"]);

  return (
    <form autoComplete="off" onSubmit={handleSubmit(submit)}>
      <StepTitleView
        title={t("step.companyInfo.title")}
        descriptions={[t("step.companyInformation.description")]}
      />
      <Stack spacing={2} mt={1} p={0}>
        <FormControl fullWidth>
          <Controller
            control={control}
            name="country"
            rules={{
              required: true,
            }}
            render={({ field }) => (
              <CountrySelector
                language={language}
                {...field}
                onChange={(e) => {
                  reset({
                    companyId: "",
                    companyName: "",
                    zip: "",
                    country: e.target.value,
                    streetAddress: "",
                    city: "",
                    vat: "",
                    vatStatus: "EMPTY",
                    vatType: "SEVAT",
                    status: undefined,
                    searchString: {
                      id: "",
                      label: "",
                    },
                  });
                }}
              />
            )}
          />
        </FormControl>
        <Controller
          control={control}
          name="searchString"
          rules={{
            required: true,
            minLength: {
              value: 3,
              message: t(
                "step.companyInformation.companyName.validation.minCharacters",
                { count: 3 }
              ),
            },
          }}
          render={({ field, fieldState }) => (
            <CompanySearchAutocomplete
              field={field}
              disabled={status === "INCOMPLETE"}
              setError={setError}
              error={fieldState.error}
              selectedCountry={country}
            />
          )}
        />
      </Stack>
      <Collapse in={status === "INCOMPLETE"}>
        <Typography variant="body1" mb={2} mt={1}>
          {t("step.companyInformation.additionalInformation.title")}
        </Typography>
        <Stack spacing={2} mb={4}>
          <Controller
            control={control}
            name="companyId"
            rules={{ required: status === "INCOMPLETE" }}
            render={({ field }) => (
              <TextField
                label={t("step.companyInformation.companyId.label")}
                disabled
                fullWidth
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="companyName"
            rules={{ required: status === "INCOMPLETE" }}
            render={({ field, fieldState }) => (
              <TextField
                label={t("step.companyInformation.companyName.label")}
                disabled={!(field.value.length === 0 || fieldState.isDirty)}
                fullWidth
                error={!!fieldState.error}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="streetAddress"
            rules={{ required: status === "INCOMPLETE" }}
            render={({ field, fieldState }) => (
              <TextField
                label={t("step.companyInformation.streetAddress.label")}
                fullWidth
                disabled={!(field.value.length === 0 || fieldState.isDirty)}
                error={!!fieldState.error}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="city"
            rules={{ required: status === "INCOMPLETE" }}
            render={({ field, fieldState }) => (
              <TextField
                label={t("step.companyInformation.city.label")}
                fullWidth
                disabled={!(field.value.length === 0 || fieldState.isDirty)}
                error={!!fieldState.error}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="zip"
            rules={{ required: status === "INCOMPLETE" }}
            render={({ field, fieldState }) => (
              <TextField
                label={t("step.companyInformation.zip.label")}
                fullWidth
                disabled={!(field.value.length === 0 || fieldState.isDirty)}
                error={!!fieldState.error}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name="vat"
            render={({ field, fieldState }) => (
              <TextField
                label={t("step.companyInformation.vat.label")}
                fullWidth
                disabled={!(field.value.length === 0 || fieldState.isDirty)}
                error={!!fieldState.error}
                {...field}
              />
            )}
          />
        </Stack>
      </Collapse>
      {submitError && (
        <Alert sx={{ my: 2 }} severity="error">
          {submitError}
        </Alert>
      )}
      <Stack direction="column" spacing={1}>
        <LoadingButton
          id="company-information-continue"
          type="submit"
          variant="contained"
          disabled={!isValid}
          loading={isSubmitting}
          fullWidth
        >
          {t("button.next")}
        </LoadingButton>
        <Button
          id="company-information-return"
          variant="outlined"
          onClick={onBack}
          disabled={isSubmitting}
          fullWidth
        >
          {t("button.back")}
        </Button>
      </Stack>
      <DevTool control={control} />
    </form>
  );
};

export default CompanyInformation;
